import { useEffect, useState } from "react";
import { FiPlus, FiX } from "react-icons/fi";
import { formatStlFileSize } from "../../../utils/smileDesign/extractStlJsonInBrowser";

interface Props {
    name: string;
    heading: string;
    description: string;
    active: boolean;
    stlFile: File | null;
    coverFile: File | null;
    submitting?: boolean;

    setName: (v: string) => void;
    setHeading: (v: string) => void;
    setDescription: (v: string) => void;
    setActive: (v: boolean) => void;
    setStlFile: (v: File | null) => void;
    setCoverFile: (v: File | null) => void;

    onSubmit: () => void;
    onClose: () => void;

    autoResizeTextarea: (
        e: React.ChangeEvent<HTMLTextAreaElement>
    ) => void;
}

export default function CreatePromptModal({
    name,
    heading,
    description,
    active,
    stlFile,
    coverFile,
    submitting = false,
    setName,
    setHeading,
    setDescription,
    setActive,
    setStlFile,
    setCoverFile,
    onSubmit,
    onClose,
    autoResizeTextarea,
}: Props) {
    const [coverPreview, setCoverPreview] = useState<string | null>(null);

    useEffect(() => {
        if (!coverFile) {
            setCoverPreview(null);
            return;
        }
        const url = URL.createObjectURL(coverFile);
        setCoverPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [coverFile]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="smile-dark-scroll w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#080808] border border-[#1f2221] rounded-2xl sm:rounded-3xl shadow-2xl my-auto">
                <div className="flex items-center justify-between p-4 sm:p-6 border-b border-[#1f2221]">
                    <div>
                        <h2 className="text-xl sm:text-2xl font-bold text-cyan-400">
                            Create Filter
                        </h2>
                        <p className="text-[#8b8f8d] text-sm mt-1">
                            Upload the original STL (stored on Box for smile generation), a cover image, heading, and an optional description.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        disabled={submitting}
                        className="p-2 rounded-lg hover:bg-[#1f2221] transition"
                    >
                        <FiX size={20} />
                    </button>
                </div>

                <div className="smile-dark-scroll max-h-[70vh] overflow-y-auto p-6 space-y-5">
                    <div>
                        <label className="block mb-2 text-sm text-[#8b8f8d]">
                            Heading
                        </label>
                        <input
                            value={heading}
                            onChange={(e) => {
                                const next = e.target.value;
                                setHeading(next);
                                if (!name.trim() || name === heading) setName(next);
                            }}
                            placeholder="Masculine upper arch"
                            className="w-full bg-[#101211] border border-[#1f2221] rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                    </div>

                    <div>
                        <label className="block mb-2 text-sm text-[#8b8f8d]">
                            Filter Name
                        </label>
                        <input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="HD Upper Masculine"
                            className="w-full bg-[#101211] border border-[#1f2221] rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                    </div>

                    <div>
                        <label className="block mb-2 text-sm text-[#8b8f8d]">
                            Description <span className="text-[#5b605e]">(optional)</span>
                        </label>
                        <textarea
                            rows={4}
                            value={description}
                            placeholder="Optional notes shown with this filter…"
                            onChange={(e) => {
                                setDescription(e.target.value);
                                autoResizeTextarea(e);
                            }}
                            className="w-full min-h-[100px] max-h-[240px] bg-[#101211] border border-[#1f2221] rounded-xl px-4 py-3 text-white resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500"
                        />
                    </div>

                    <div>
                        <label className="block mb-2 text-sm text-[#8b8f8d]">
                            Original STL
                        </label>
                        <label className="flex flex-col items-center justify-center gap-2 w-full min-h-[120px] bg-[#101211] border border-dashed border-[#2a2e2c] rounded-xl px-4 py-6 cursor-pointer hover:border-cyan-500/40 transition">
                            <input
                                type="file"
                                accept=".stl,model/stl,application/sla"
                                className="hidden"
                                onChange={(e) => setStlFile(e.target.files?.[0] || null)}
                            />
                            <span className="text-white text-sm">
                                {stlFile
                                    ? `${stlFile.name} · ${formatStlFileSize(stlFile.size)}`
                                    : "Upload original STL"}
                            </span>
                            <span className="text-xs text-[#8b8f8d] text-center">
                                Stored on Box and served automatically during smile generation.
                            </span>
                        </label>
                        {stlFile && (
                            <button
                                type="button"
                                onClick={() => setStlFile(null)}
                                className="mt-2 text-xs text-red-400 hover:text-red-300"
                            >
                                Remove STL
                            </button>
                        )}
                    </div>

                    <div>
                        <label className="block mb-2 text-sm text-[#8b8f8d]">
                            Cover Image
                        </label>
                        <label className="flex flex-col items-center justify-center gap-2 w-full min-h-[120px] bg-[#101211] border border-dashed border-[#2a2e2c] rounded-xl px-4 py-6 cursor-pointer hover:border-cyan-500/40 transition">
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/avif"
                                className="hidden"
                                onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
                            />
                            <span className="text-white text-sm">
                                {coverFile ? coverFile.name : "Upload cover image"}
                            </span>
                        </label>
                        {coverPreview && (
                            <img
                                src={coverPreview}
                                alt="Cover preview"
                                className="mt-3 w-full max-h-44 object-contain rounded-xl border border-[#2a2e2c] bg-[#C1C1C3]"
                            />
                        )}
                        {coverFile && (
                            <button
                                type="button"
                                onClick={() => setCoverFile(null)}
                                className="mt-2 text-xs text-red-400 hover:text-red-300"
                            >
                                Remove cover
                            </button>
                        )}
                    </div>

                    <div className="flex items-center justify-between bg-[#101211] border border-[#1f2221] rounded-xl px-4 py-3">
                        <span className="text-white">Active Filter</span>
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                checked={active}
                                onChange={(e) => setActive(e.target.checked)}
                                className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-700 rounded-full peer-checked:bg-cyan-500 transition-all" />
                            <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-all peer-checked:translate-x-5" />
                        </label>
                    </div>

                    {submitting && (
                        <div className="bg-cyan-500/10 border border-cyan-500/20 text-cyan-200 rounded-xl p-4 text-sm">
                            Uploading STL to Box and saving filter…
                        </div>
                    )}
                </div>

                <div className="border-t border-[#1f2221] p-5">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={onClose}
                            disabled={submitting}
                            className="flex-1 py-3 rounded-xl bg-[#1f2221] hover:bg-[#2a2d2c] text-white font-medium transition disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onSubmit}
                            disabled={submitting}
                            className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-cyan-500 to-blue-600 py-3 rounded-xl font-semibold hover:opacity-90 transition disabled:opacity-50"
                        >
                            <FiPlus size={18} />
                            {submitting ? "Saving…" : "Save Filter"}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
